import { produce } from 'immer';
import React, { useState } from 'react';
import { RSButton } from 'reactsymbols-kit';
import MerlinSelect, { type MerlinSelectOption } from '../../MerlinSelect';
import { useAsynchronousAction } from '../../../hooks';
import Styles from '../../../styles/colors';
import type { IModelVersion } from '../../../stores/projects/model_version';
import type { IModel } from '../../../stores/model';
import type { IEventType } from '../../../stores/model/metamodel/event_type';
import { rsValue } from '../../Inspector/rsForm';

/** Per-specialised-event action choice, keyed by event id. */
type EventActionMap = Record<string, string>;

/**
 * The delete callback: receives the inheritance/element id and the cascade
 * options, and resolves the delete (report). The hook forwards args untyped; the
 * leading `id: string` matches the committed caller (`DevEdg.performDelete`).
 */
type PerformDelete = (id: string, ...rest: unknown[]) => Promise<unknown>;

const ACTION_OPTIONS = [
  {
    value: 'KEEP',
    label: 'Keep but change into a normal event',
  },
  {
    value: 'DELETE',
    label: 'Remove the specialised event',
  },
];

interface SpecialisedEventActionSelectorProps {
  /** The specialised event to choose an action for. */
  event: IEventType;
  /** The currently selected action ('KEEP' | 'DELETE'). */
  value: string;
  /** Change handler — receives the new action value. */
  onChange: (value: string) => void;
  /** Inline style (used for z-index stacking of the dropdowns). */
  style?: React.CSSProperties;
}

export const SpecialisedEventActionSelector = ({ event, value, onChange, style }: SpecialisedEventActionSelectorProps) => {
  let methodName;
  if (event.specialisedMethods.length > 0) {
    methodName = event.specialisedMethods[0].displayStringWithMetadata;
  }
  return (
    <div className="row specialisation" style={style}>
      <div className="col supertype-event">
        <label>Specialised event</label>
        <span className="event-type-name">{event.name}</span>
        {methodName != null && <span className="event-metadata">[{methodName}]</span>}
      </div>
      <div className="col">
        <MerlinSelect
          className="rs-input-wrap"
          options={ACTION_OPTIONS}
          value={value}
          onChange={(option: MerlinSelectOption | null) =>
            option != null && onChange(option.value as string)
          }
          searchable={false}
          clearable={false}
        />
      </div>
    </div>
  );
};

interface DeleteInheritanceFormProps {
  /** The ModelVersion holding the inheritance to delete. */
  modelVersion: IModelVersion;
  /** The id of the inheritance to delete. */
  id: string;
  /** Callback to cancel the deletion. */
  onCancel?: () => void;
  /** Callback that performs the actual delete. */
  onPerformDelete: PerformDelete;
}

const DeleteInheritanceForm = ({ modelVersion, id, onCancel, onPerformDelete }: DeleteInheritanceFormProps) => {
  // `content` is the loaded model here (non-null), matching the original JS.
  const inheritance = (modelVersion.content as unknown as IModel).metamodel.getInheritanceById(id);
  // Inheritance can be undefined briefly before unmounting ...
  const specialisedEvents = inheritance != null ? inheritance.specialisedEvents : [];
  const [specialisedEventOptions, setSpecialisedEventOptions] = useState<EventActionMap>(() => {
    const result: EventActionMap = {};
    for (const e of specialisedEvents) {
      result[e.id] = 'KEEP';
    }
    return result;
  });

  const updateSpecialisedEventOptions = (id: string, value: string) => {
    setSpecialisedEventOptions(
      produce((draft: EventActionMap) => {
        draft[id] = value;
      })
    );
  };

  // `onPerformDelete` declares a leading `id: string`; the hook wants the looser
  // `(...args: unknown[]) => Promise<unknown>`. Cast at this boundary only — the
  // same typed args flow through `execute`.
  const deleteAction = useAsynchronousAction(
    onPerformDelete as (...args: unknown[]) => Promise<unknown>
  );
  const busy = deleteAction.busy;
  const valid = specialisedEvents
    .map((e) => e.id)
    .every((id) => specialisedEventOptions[id] != null);

  return (
    <div className="merlin-modal delete-inheritance-form">
      <div className="title">Remove inheritance relation</div>
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          if (valid && !busy) {
            deleteAction.execute(id, specialisedEventOptions);
          }
        }}
      >
        <div className="modal-body">
          <div className="container">
            {specialisedEvents.map((e, i) => (
              <SpecialisedEventActionSelector
                key={e.id}
                event={e}
                value={specialisedEventOptions[e.id]}
                onChange={(value) => updateSpecialisedEventOptions(e.id, value)}
                style={{ zIndex: 100 - i, position: 'relative' }}
              />
            ))}
          </div>
        </div>
        <div className="button-wrapper">
          <RSButton
            value="Cancel"
            type="button"
            background={Styles.defaultGradient}
            color={Styles.darkTextColor}
            onClick={onCancel}
          />
          <RSButton
            type="submit"
            value={rsValue(busy ? null : 'Remove')}
            background={Styles.blueGradient}
            disabled={!valid || busy}
            iconName={rsValue(busy ? 'FaSpinner' : null)}
            iconSize={15}
            className={rsValue(busy ? 'spin' : null)}
          />
        </div>
      </form>
    </div>
  );
};

export default DeleteInheritanceForm;
